<template>
  <div>
    <template v-if="!uploadFinished">
      <app-alert-in-page variant="info">
        {{ $t("archives.steps.ArchivesAddStep4.confirmationHint") }}
      </app-alert-in-page>

      <section v-if="filesToUpload.length !== 0" class="my-10">
        <p class="mb-6 text--medium">
          <span v-if="!uploadStarted">
            {{ $t("archives.steps.ArchivesAddStep4.titleUploadNotStarted") }}
          </span>
          <span v-else>
          {{ $t("archives.steps.ArchivesAddStep4.titleUploadStarted") }}
          </span>
        </p>

        <ArchivesAddStep4File v-for="(file, i) in filesToUpload"
                              :key="`fileToUpload-${i}`"
                              upload
                              :file="file"
        />
      </section>

      <section v-if="filesOnClosd.length !== 0" class="my-10">
        <p class="mb-6 text--medium">
          {{ $t("archives.steps.ArchivesAddStep4.titleUploadReady") }}
        </p>

        <ArchivesAddStep4File v-for="file in filesOnClosd"
                              :key="`fileOnClosd-${ file.objectToPost.fileId || file.objectToPost.envId || file.objectToPost.bibleId }`"
                              :file="file"
        />
      </section>
    </template>
    <template v-else-if="archivingSuccess">
      <div class="d-flex flex-column align-center">
        <app-material-icon 
          name="check_circle"
          class="archiving-status-icon"
          color="success"
          size="xxl"
        />
        <p class="mt-4 text--small text--bold"
                  v-t="{
                  path: 'archives.steps.ArchivesAddStep4.successTemplate.archivingFilesNumber',
                  plural: importedFiles.length,
                  args: { nb: importedFiles.length },
                }"
        />
        <p class="mt-2">
          {{ $t("archives.steps.ArchivesAddStep4.successTemplate.archivingRedirect") }}
          <router-link :to="{ name: ARCHIVES_ROUTE_NAME }">
            {{
              $t("archives.steps.ArchivesAddStep4.successTemplate.archivingRedirectLink")
            }}
          </router-link>
        </p>
      </div>
    </template>
    <template v-else-if="archivingFailure">
      <div class="d-flex flex-column align-center">
        <app-material-icon 
          name="warning"
          filled
          class="archiving-status-icon"
          color="warning"
          size="xxl"
        />
        <p class="mt-4 text--small text--bold"
                  v-t="{
                  path: 'archives.steps.ArchivesAddStep4.failureTemplate.archivingFilesNumber',
                  plural: failedFiles,
                  args: { nb: failedFiles.length },
                }"
        />
        <div class="mt-2 w-100">
          <p class="my-4 text--medium">
            {{ $t('archives.steps.ArchivesAddStep4.failureTemplate.failedFiles') }}
          </p>
          <ArchivesAddStep4File v-for="(file, i) in failedFiles"
                                :key="`failedFile-${i}`"
                                :file="file"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ARCHIVES_ROUTE_NAME } from '@/router/routerData'
import { useArchivesStore } from '@/stores/archivesStore'

import ArchivesAddStep4File from './ArchivesAddStep4File.vue'

export default {
  name: 'ArchivesAddStep4',
  components: {
    ArchivesAddStep4File,
  },
  setup() {
    const archivesStore = useArchivesStore()
    return { archivesStore }
  },
  props: {
    importedFiles: {
      required: true,
      validator: (value) => {
        return value === undefined || Array.isArray(value)
      },
    },
  },
  data () {
    return {
      uploadStarted: false,
      uploadFinished: false,
      SIMULTANEOUS_FILES_UPLOAD: Number(import.meta.env.VUE_APP_SIMULTANEOUS_FILES_UPLOAD) || 2,
      failedFiles: [],
    }
  },
  computed: {
    queuedFilesInProgress () {
      return this.archivesStore.uploadArchivePendingIds.length
    },
    filesToUpload () {
      return this.importedFiles.filter(f => f.objectToPost.hasOwnProperty('fileRaw'))
    },
    filesOnClosd () {
      return this.importedFiles.filter(f => !!f.objectToPost.fileId || !!f.objectToPost.envId || !!f.objectToPost.bibleId)
    },
    archivingSuccess () {
      return this.importedFiles.every(f => f.status === 'success')
    },
    archivingFailure () {
      return this.failedFiles.length !== 0
    },
  },
  watch: {
    importedFiles: {
      handler (newImportedFiles) {
        if (newImportedFiles.every((file) => file.status === 'success' || file.status === 'error')) {
          this.uploadFinished = true
          this.failedFiles = newImportedFiles.filter((file) => file.status === 'error')
          this.$emit('uploadFinished', this.failedFiles.length === 0)
        }
      },
      deep: true,
    },
  },
  created () {
    this.ARCHIVES_ROUTE_NAME = ARCHIVES_ROUTE_NAME
  },
  methods: {
    async startUpload () {
      this.uploadStarted = true
      this.$emit('uploadStarted')
      // When the upload starts, references on the importedFiles array may be changed by the parent
      // We want to keep the same base reference so that at the end of the uploads,
      // We still can change properties and have the same reference without it being overwritten.
      await this.$nextTick()
      this.launchQueue()
      for (const file of this.filesOnClosd) {
        this.archivesStore.postArchive(file)
      }
    },
    launchQueue () {
      for (const f of this.filesToUpload) {
        if (f.status === 'queued') {
          if (this.queuedFilesInProgress < (import.meta.env.VUE_APP_SIMULTANEOUS_FILES_UPLOAD || 2)) {
            f.status = 'uploading'
            this.archivesStore.uploadArchive(f)
              .finally(() => {
                if (this.queuedFilesInProgress < (import.meta.env.VUE_APP_SIMULTANEOUS_FILES_UPLOAD || 2)) {
                  this.launchQueue()
                }
              })
          }
        }
      }
    },
  },
}
</script>
